import {
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  IActiveWallet,
  IDexBigTrade,
  IResponse,
  IWallet,
  IWatchlistWallet,
} from '@kattana-io/common';
import { LOG_OUT, SET_AUTH, SetAuthPayload } from '../actions/auth';
import { actionShowModal } from '../actions/modal';
import { actionCreateNotification } from '../actions/notifications';
import {
  actionRemoveSuccessWatchlistWallet,
  actionUpdateSuccessWatchlistWallet,
  actionSetWatchlistWatchedWallets,
  actionSetWatchlistTopWallets,
  actionSetWalletWatchlistLoading,
  actionSetWalletWatchlistTab,
  actionSetWatchlistWallets,
  ACTIVATE,
  ADD_WALLET,
  REMOVE_WALLET,
  UPDATE_WALLET,
  SET_TAB,
  SET_CHAIN,
  actionSetWatchlistBigTradesWallets,
  actionSetWatchlistActiveWallets,
  actionActivateWalletWatchlist,
} from '../actions/wallet_watchlist';
import { IStore } from '../reducers';
import { ModalTypes } from '../reducers/modal';
import { authTokenSelector } from '../selectors/auth';
import {
  IBigTradesWallet,
  IBigVolumeWallet,
  IWatchedWallet,
  LS_WALLET_WATCHLIST_TAB_KEY,
  WalletWatchlistTab,
} from '../reducers/wallet_watchlist';
import { KattanaAPI, WalletAPI, WatchListAPI } from '../store/createStore';

export const parseWatchlistWallets = (
  wallets: IWatchlistWallet[]
): IWatchedWallet[] => {
  return wallets.map((item) => ({
    address: item.wallet.address,
    wallet_id: item.wallet.wallet_id,
    chain: item.wallet.chain,
    nickname: item.wallet.nickname,
    order: item.walletWatchlist.order,
    tag: item.walletWatchlist.tag,
    balance_usd: item.wallet.balance_usd,
  }));
};

export const parseBigTradesWallets = (
  wallets: IDexBigTrade[]
): IBigTradesWallet[] => {
  return Array.from(
    [...wallets]
      .reverse()
      .reduce(
        (cons, trade) => cons.set(`${trade.wallet}-${trade.chain}`, trade),
        new Map()
      )
      .values()
  ).map((item) => ({
    address: item.wallet,
    chain: item.chain,
    value_usd: Number(item.value_usd),
    nickname: '',
  }));
};

export const parseActiveWallets = (
  wallets: IActiveWallet[]
): IBigVolumeWallet[] =>
  wallets.map((wallet) => ({
    chain: wallet.chain,
    nickname: wallet.nickname,
    trades_amount: wallet.trades_amount,
    volume_24: wallet.volume_24,
    address: wallet.wallet,
  }));

function* onActivate(): any {
  const loggedIn = yield select((state: IStore) => state.auth.loggedIn);
  if (loggedIn) {
    const watched = yield select(
      (state: IStore) => state.wallet_watchlist.watchedWallets
    );
    if (watched.length > 0) {
      yield put(actionSetWalletWatchlistTab(WalletWatchlistTab.Star));
    }
  } else {
    let lastTab = WalletWatchlistTab.TopWallets;
    if (window.localStorage) {
      if (window.localStorage.getItem(LS_WALLET_WATCHLIST_TAB_KEY)) {
        lastTab = window.localStorage.getItem(
          LS_WALLET_WATCHLIST_TAB_KEY
        ) as WalletWatchlistTab;
      }
    }
    yield put(actionSetWalletWatchlistTab(lastTab));
  }
}

function* onAuth(action: any): any {
  try {
    const data = action.payload as SetAuthPayload;
    const token = yield select(authTokenSelector);
    yield put(actionSetWalletWatchlistLoading(true));
    if (data.loggedIn && data.wallet && data.network) {
      const wallets: IWatchlistWallet[] | null = yield call(
        WatchListAPI.getWallets,
        data.network,
        data.wallet,
        token
      );

      if (Array.isArray(wallets)) {
        const sortedWallets = wallets
          .sort((a, b) => a.walletWatchlist.order - b.walletWatchlist.order)
          .map((item, idx) => ({ ...item, order: idx }));
        const items = parseWatchlistWallets(sortedWallets);
        yield put(actionSetWatchlistWatchedWallets(items));
      } else {
        yield put(actionSetWatchlistWatchedWallets([]));
      }
    }
  } catch (error) {
    yield put(
      actionCreateNotification({
        kind: 'warning',
        message: 'Failed to fetch watchlist items...',
      })
    );
    console.error(error);
    yield put(actionSetWatchlistWatchedWallets([]));
  } finally {
    yield put(actionSetWalletWatchlistLoading(false));
    yield put(actionActivateWalletWatchlist());
  }
}

function* fetchBigTrades(): any {
  const chain = yield select((state: IStore) => state.wallet_watchlist.chain);
  yield put(actionSetWalletWatchlistLoading(true));
  const res: IDexBigTrade[] | null = yield call(
    KattanaAPI.bigTrades,
    '10000',
    '50',
    { network: chain }
  );
  const currentTab = yield select(
    (state: IStore) => state.wallet_watchlist.tab
  );
  if (res && Array.isArray(res)) {
    const items = parseBigTradesWallets(res);
    yield put(actionSetWatchlistBigTradesWallets(items));
    if (currentTab === WalletWatchlistTab.BigTradesWallets) {
      yield put(actionSetWatchlistWallets(items));
    }
  } else {
    yield put(actionSetWatchlistBigTradesWallets([]));
    if (currentTab === WalletWatchlistTab.BigTradesWallets) {
      yield put(actionSetWatchlistWallets([]));
    }
  }
  yield put(actionSetWalletWatchlistLoading(false));
}

function* fetchActiveWallets(): any {
  const chain = yield select((state: IStore) => state.wallet_watchlist.chain);
  yield put(actionSetWalletWatchlistLoading(true));
  const res: IActiveWallet[] | null = yield call(
    WalletAPI.fetchActiveWallets,
    chain,
    50
  );
  const currentTab = yield select(
    (state: IStore) => state.wallet_watchlist.tab
  );
  if (res && Array.isArray(res)) {
    const items = parseActiveWallets(res);
    yield put(actionSetWatchlistActiveWallets(items));
    if (currentTab === WalletWatchlistTab.ActiveWallets) {
      yield put(actionSetWatchlistWallets(items));
    }
  } else {
    yield put(actionSetWatchlistActiveWallets([]));
    if (currentTab === WalletWatchlistTab.ActiveWallets) {
      yield put(actionSetWatchlistWallets([]));
    }
  }
  yield put(actionSetWalletWatchlistLoading(false));
}

function* onSetTab(action: any): any {
  const tab = action.payload as WalletWatchlistTab;
  const topWallets = yield select(
    (state: IStore) => state.wallet_watchlist.topWallets
  );
  const watchedWallets = yield select(
    (state: IStore) => state.wallet_watchlist.watchedWallets
  );
  const authToken = yield select(authTokenSelector);

  yield put(actionSetWalletWatchlistLoading(false));
  if (tab === WalletWatchlistTab.Star) {
    yield put(actionSetWatchlistWallets(watchedWallets));
  } else if (tab === WalletWatchlistTab.TopWallets) {
    if (topWallets.length > 0) {
      yield put(actionSetWatchlistWallets(topWallets));
    } else {
      yield put(actionSetWalletWatchlistLoading(true));
      const res: IWallet[] | null = yield call(
        WalletAPI.fetchTopWallets,
        authToken
      );
      if (res && Array.isArray(res)) {
        yield put(actionSetWatchlistTopWallets(res));
        yield put(actionSetWatchlistWallets(res));
      } else {
        yield put(actionSetWatchlistTopWallets([]));
        yield put(actionSetWatchlistWallets([]));
      }
      yield put(actionSetWalletWatchlistLoading(false));
    }
  } else if (tab === WalletWatchlistTab.BigTradesWallets) {
    yield call(fetchBigTrades);
  } else if (tab === WalletWatchlistTab.ActiveWallets) {
    yield call(fetchActiveWallets);
  }

  if (window.localStorage) {
    window.localStorage.setItem(LS_WALLET_WATCHLIST_TAB_KEY, tab);
  }
}

function* onSetChain(): any {
  const tab = yield select((state: IStore) => state.wallet_watchlist.tab);

  if (tab === WalletWatchlistTab.BigTradesWallets) {
    yield call(fetchBigTrades);
  }

  if (tab === WalletWatchlistTab.ActiveWallets) {
    yield call(fetchActiveWallets);
  }
}

function* onAdd(action: any): any {
  const { data, tag, callback } = action.payload;

  try {
    const token = yield select(authTokenSelector);
    const { loggedIn, wallet, network } = yield select(
      (store: IStore) => store.auth
    );

    if (!loggedIn) {
      // show connect modal
      yield put(actionShowModal(ModalTypes.connect, {}));
      return;
    }

    let walletId: number | null = null;

    const payload = { tag };

    if (data.type === 'overview') {
      const res = yield call(
        WatchListAPI.addWallet,
        network,
        wallet,
        data.walletId,
        payload,
        token
      );
      if (res.success) {
        walletId = data.walletId;
      }
    } else if (data.type === 'traders') {
      const res = yield call(
        WatchListAPI.addWalletByAddrChain,
        network,
        wallet,
        data.chain,
        data.address,
        payload,
        token
      );
      if (res.success) {
        walletId = res.id;
      }
    }
    if (walletId !== null) {
      yield delay(1000);
      const wallets: IWatchlistWallet[] | null = yield call(
        WatchListAPI.getWallets,
        network,
        wallet,
        token
      );
      if (wallets && Array.isArray(wallets)) {
        const sortedWallets = wallets
          .sort((a, b) => a.walletWatchlist.order - b.walletWatchlist.order)
          .map((item, idx) => ({ ...item, order: idx }));
        const watchlistItems = parseWatchlistWallets(sortedWallets);
        yield put(actionSetWatchlistWatchedWallets(watchlistItems));
        const currentTab = yield select(
          (store: IStore) => store.wallet_watchlist.tab
        );
        if (currentTab === WalletWatchlistTab.Star) {
          yield put(actionSetWatchlistWallets(watchlistItems));
        } else {
          const currentWatchlistWallets = yield select(
            (store: IStore) => store.wallet_watchlist.wallets
          );
          yield put(actionSetWatchlistWallets([...currentWatchlistWallets]));
        }
      }
    }
    callback?.();
  } catch (error) {
    callback?.();
    yield put(
      actionCreateNotification({
        kind: 'warning',
        message: 'Failed to add item to watchlist...',
      })
    );
    console.error(error);
  }
}

function* onUpdate(action: any): any {
  try {
    const { id, payload } = action.payload;
    const token = yield select(authTokenSelector);
    const { loggedIn, wallet, network } = yield select(
      (store: IStore) => store.auth
    );

    if (!loggedIn) {
      // show connect modal
      yield put(actionShowModal(ModalTypes.connect, {}));
      return;
    }

    yield put(actionUpdateSuccessWatchlistWallet(id, payload));
    const currentWatchlistWallets = yield select(
      (store: IStore) => store.wallet_watchlist.wallets
    );
    yield put(actionSetWatchlistWallets([...currentWatchlistWallets]));

    const res: IResponse<undefined> = yield call(
      WatchListAPI.updateWallet,
      network,
      wallet,
      id,
      payload,
      token
    );

    if (res.error) {
      console.error(res.error);
    }
  } catch (error) {
    yield put(
      actionCreateNotification({
        kind: 'warning',
        message: 'Failed to update watchlist item...',
      })
    );
    console.error(error);
  }
}

function* onRemove(action: any): any {
  const { id, callback } = action.payload;

  try {
    const token = yield select(authTokenSelector);
    const { wallet, network } = yield select((store: IStore) => store.auth);

    const res: IResponse<any> = yield call(
      WatchListAPI.removeWallet,
      network,
      wallet,
      id,
      token
    );

    if (res.success) {
      yield put(actionRemoveSuccessWatchlistWallet(id));
    }
    callback?.();
  } catch (error) {
    callback?.();
    yield put(
      actionCreateNotification({
        kind: 'warning',
        message: 'Failed to remove item from watchlist...',
      })
    );
    console.error(error);
  }
}

function* onLogOut(): any {
  try {
    yield put(actionSetWatchlistWatchedWallets([]));
    const currentTab = yield select(
      (state: IStore) => state.wallet_watchlist.tab
    );
    if (currentTab === WalletWatchlistTab.Star) {
      yield put(actionSetWatchlistWallets([]));
    }
  } catch (error) {
    console.error(error);
  }
}

export default function* walletWatchlistSaga() {
  yield takeLatest(ACTIVATE, onActivate);
  yield takeLatest(SET_AUTH, onAuth);
  yield takeLatest(LOG_OUT, onLogOut);
  yield takeEvery(ADD_WALLET, onAdd);
  yield takeEvery(REMOVE_WALLET, onRemove);
  yield takeEvery(UPDATE_WALLET, onUpdate);
  yield takeLatest(SET_TAB, onSetTab);
  yield takeLatest(SET_CHAIN, onSetChain);
}
